import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { ArrowRight, Cpu, Code, Printer, Brain, Zap, Users, CheckCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const activities = [
  { icon: <Cpu className="w-7 h-7 text-[#1B6FE3]" />, title: 'Construction de robots', desc: "Les enfants assemblent et construisent leurs propres robots en utilisant des kits adaptés à leur âge." },
  { icon: <Code className="w-7 h-7 text-[#F97316]" />, title: 'Programmation', desc: "Ils apprennent à programmer pour donner vie à leurs créations, du code visuel au code textuel." },
  { icon: <Printer className="w-7 h-7 text-purple-500" />, title: 'Impression 3D', desc: "Découverte de la modélisation 3D et impression de leurs propres créations personnalisées." },
  { icon: <Brain className="w-7 h-7 text-emerald-500" />, title: 'Défis et challenges', desc: "Des missions amusantes pour stimuler leur réflexion et leur capacité à résoudre des problèmes." },
  { icon: <Zap className="w-7 h-7 text-amber-500" />, title: 'Projets créatifs', desc: "Chaque enfant réalise ses propres projets innovants en laissant libre cours à son imagination." },
  { icon: <Users className="w-7 h-7 text-sky-500" />, title: 'Travail en équipe', desc: "Collaboration et communication entre élèves pour mener à bien des projets communs." },
];

const sessionSteps = [
  { num: 1, title: 'Accueil des enfants', desc: "Un moment d'échange pour bien démarrer la séance." },
  { num: 2, title: "Présentation de l'activité", desc: "Découverte du thème et des objectifs du jour." },
  { num: 3, title: 'Construction / programmation', desc: "Phase pratique où les enfants manipulent et créent." },
  { num: 4, title: 'Défi ou projet', desc: "Mise en application des notions apprises à travers un challenge." },
  { num: 5, title: 'Bilan de séance', desc: "Partage des réussites et rangement du matériel." },
];

const examples = [
  "Créer un robot qui évite les obstacles",
  "Programmer un robot suiveur de ligne",
  "Modéliser et imprimer un porte-clés en 3D",
  "Construire un mini véhicule intelligent",
  "Participer à des défis robotique",
];

const skills = ['Logique', 'Créativité', 'Autonomie', 'Résolution de problèmes', 'Travail en équipe', 'Vision spatiale (3D)'];

const galleryImages = [
  { src: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/551d0c4651f57c734025805ed305ec61.jpg', alt: 'Enfants en classe de robotique' },
  { src: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/614a601a16a709d9a436d89d15dabf90.jpg', alt: 'Enfant construisant un robot' },
  { src: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/9e7b18e4271d85d2d3155f17fa477a62.jpg', alt: "Groupe d'enfants en atelier" },
  { src: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/6d293f6187c04962d833bc4b1cbd1fa1.jpg', alt: 'Enfant avec un robot humanoïde' },
  { src: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/3440a3bae829695c4711b2d091e7eb95.jpg', alt: 'Session de programmation' },
  { src: 'https://horizons-cdn.hostinger.com/1b0ab6fa-0746-4a31-a6ec-811cdb30e480/266c2b343e53787dba8bd30e0ae4d2ff.jpg', alt: 'Instructeur guidant des enfants' },
];

export const ActivitiesPage = () => {
  return (
    <Layout>
      {/* HERO */}
      <section className="bg-[#1B6FE3] py-16 md:py-24 text-center" data-testid="activities-hero">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <div className="inline-block bg-white/15 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-bold text-white/90 mb-5">
              ATELIERS & STAGES
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4">
              Des activités ludiques pour apprendre en créant
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8">
              Nos ateliers permettent aux enfants de découvrir la robotique, la programmation, l'impression 3D et l'innovation à travers des projets concrets.
            </p>
            <Link to="/programmes" className="btn-primary text-base px-8 py-4 inline-flex items-center gap-2">
              Découvrir les programmes <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* APPRENDRE AUTREMENT */}
      <section className="py-16 md:py-20 bg-white" data-testid="learn-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="font-heading text-3xl font-extrabold text-slate-900 mb-3">Apprendre autrement</h2>
            <div className="w-16 h-1 bg-[#1B6FE3] mx-auto mb-4 rounded-full"></div>
            <p className="text-slate-600 text-sm leading-relaxed">
              Nous croyons fermement que l'apprentissage par la pratique (learning by doing) est la méthode la plus efficace. Nos activités sont conçues pour transformer les concepts abstraits en expériences tangibles et amusantes.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {activities.map((a, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: i * 0.08 }} viewport={{ once: true }}
                className="tk-card flex items-start gap-3" data-testid={`activity-card-${i}`}>
                <div className="w-12 h-12 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0">{a.icon}</div>
                <div>
                  <h3 className="font-heading text-base font-extrabold text-slate-900 mb-1">{a.title}</h3>
                  <p className="text-slate-600 text-sm">{a.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DÉROULEMENT SÉANCE */}
      <section className="py-16 md:py-20 bg-white" data-testid="session-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-10">Comment se déroule une séance</h2>
          <div className="flex flex-col md:flex-row gap-4 max-w-5xl mx-auto">
            {sessionSteps.map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: i * 0.1 }} viewport={{ once: true }}
                className="flex-1 tk-card text-center">
                <div className="w-10 h-10 bg-[#1B6FE3] text-white rounded-full flex items-center justify-center mx-auto mb-2 text-sm font-bold">{s.num}</div>
                <h3 className="font-heading text-sm font-extrabold text-slate-900 mb-1">{s.title}</h3>
                <p className="text-slate-600 text-xs">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* EXEMPLES D'ACTIVITÉS */}
      <section className="py-14 section-cream" data-testid="examples-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl">
          <h2 className="font-heading text-2xl font-extrabold text-slate-900 text-center mb-2">Exemples d'activités</h2>
          <p className="text-slate-600 text-center text-sm mb-6">Donne du concret à l'apprentissage</p>
          <ul className="space-y-2 max-w-lg mx-auto">
            {examples.map((ex, i) => (
              <li key={i} className="flex items-center gap-2 bg-white rounded-xl px-4 py-3 border border-slate-100">
                <CheckCircle size={16} className="text-[#1B6FE3] flex-shrink-0" />
                <span className="text-slate-700 text-sm">{ex}</span>
              </li>
            ))}
          </ul>
          <p className="text-center text-slate-500 text-sm italic mt-6">
            "Chaque projet est une nouvelle aventure où l'enfant est le héros de sa propre création."
          </p>
        </div>
      </section>

      {/* COMPÉTENCES */}
      <section className="py-14 bg-white" data-testid="skills-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-3xl text-center">
          <h2 className="font-heading text-2xl font-extrabold text-slate-900 mb-2">Ce que les enfants apprennent</h2>
          <p className="text-slate-600 text-sm mb-6">Des compétences essentielles pour leur avenir, développées naturellement par le jeu.</p>
          <div className="flex flex-wrap justify-center gap-2">
            {skills.map((s, i) => (
              <span key={i} className="bg-blue-50 text-[#1B6FE3] font-bold text-sm px-4 py-2 rounded-full">{s}</span>
            ))}
          </div>
        </div>
      </section>

      {/* GALERIE */}
      <section className="py-16 md:py-20 section-light-blue" data-testid="gallery-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <h2 className="font-heading text-3xl font-extrabold text-slate-900 text-center mb-2">Galerie</h2>
          <p className="text-slate-600 text-center text-sm mb-8">Plongez dans l'univers de nos ateliers</p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 max-w-5xl mx-auto">
            {galleryImages.map((img, i) => (
              <div key={i} className="img-rounded aspect-square overflow-hidden">
                <img src={img.src} alt={img.alt} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#1B6FE3]" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-black text-white mb-5">
            Offrez à votre enfant une expérience unique
          </h2>
          <Link to="/contact" className="btn-primary text-base px-10 py-4 inline-flex items-center gap-2">
            Réserver un atelier gratuit <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default ActivitiesPage;
